import React from 'react';
import SEO from '../components/SEO';
import { Wrench, Shield, Award, Clock, Phone, MapPin, Calendar, MessageSquare, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import PageBanner from '../components/PageBanner';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const Service = () => {
  const { language } = useLanguage();
  const t = translations[language].service;

  return (
    <div className="min-h-screen bg-white pt-20 lg:pt-[120px]">
      <SEO
        title="BAIC Service | Auto Maintenance & Repairs in Oman"
        description="Comprehensive BAIC Oman vehicle booking, feedback, repairs, and BAIC X7 Recall Notice support in  Oman. Book today."
        canonicalUrl="https://baicoman.com/car-service"
      />
      <h1 className="sr-only">Comprehensive BAIC Oman vehicle booking, feedback, repairs, and BAIC X7 Recall Notice support in  Oman. Book today</h1>
      {/* Hero Section */}
      <PageBanner
        image="/assets/images/service/service-center.jpg"
        imageAlt="Service Hero"
        contentClassName="items-start"
      >
        <h2 className="text-white text-6xl md:text-8xl font-bold tracking-tighter drop-shadow-lg animate-in fade-in slide-in-from-left duration-700">{t.title}</h2>
      </PageBanner>

      {/* Action Cards Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-20 pb-20">
        <div className="text-center mb-12">
          <p className="text-xl md:text-2xl text-gray-800 font-light tracking-wide bg-white/80 backdrop-blur-sm inline-block px-8 py-2 rounded-full shadow-sm">
            {t.intro}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Booking Card */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col group hover:-translate-y-2 transition-all duration-500 border border-gray-100">
            <div className="h-64 overflow-hidden relative">
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors z-10 duration-500" />
              <img
                src="/assets/images/service/booking.x1rTBXGR.jpg"
                alt="Booking"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
            </div>
            <div className="p-8 flex-1 flex flex-col items-center text-center bg-white relative z-20">
              <h3 className="text-2xl font-bold text-gray-800 mb-3 group-hover:text-red-600 transition-colors">{t.cards.booking.title}</h3>
              <p className="text-gray-500 text-base mb-8 leading-relaxed font-light">
                {t.cards.booking.desc}
              </p>
              <Link to="/book-service" className="mt-auto w-full">
                <Button className="w-full rounded-full bg-black text-white hover:bg-red-600 transition-all duration-300 py-6 text-sm tracking-wider uppercase font-medium shadow-lg hover:shadow-red-600/30">
                  {t.cards.booking.btn}
                </Button>
              </Link>
            </div>
          </div>

          {/* Feedback Card */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col group hover:-translate-y-2 transition-all duration-500 border border-gray-100">
            <div className="h-64 overflow-hidden relative">
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors z-10 duration-500" />
              <img
                src="/assets/images/service/feedback.BGyvPy5t.jpg"
                alt="Feedback"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
            </div>
            <div className="p-8 flex-1 flex flex-col items-center text-center bg-white relative z-20">
              <h3 className="text-2xl font-bold text-gray-800 mb-3 group-hover:text-red-600 transition-colors">{t.cards.feedback.title}</h3>
              <p className="text-gray-500 text-base mb-8 leading-relaxed font-light">
                {t.cards.feedback.desc}
              </p>
              <Link to="/feedback" className="mt-auto w-full">
                <Button className="w-full rounded-full bg-black text-white hover:bg-red-600 transition-all duration-300 py-6 text-sm tracking-wider uppercase font-medium shadow-lg hover:shadow-red-600/30">
                  {t.cards.feedback.btn}
                </Button>
              </Link>
            </div>
          </div>

          {/* Recall Notice Card */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col group hover:-translate-y-2 transition-all duration-500 border border-gray-100">
            <div className="h-64 overflow-hidden relative">
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors z-10 duration-500" />
              <img
                src="/assets/images/service/new_sales.CJ0jgf0g.jpeg"
                alt="Recall Notice"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                onError={(e) => { e.target.src = 'https://images.pexels.com/photos/100656/pexels-photo-100656.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1' }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end justify-center pb-6">
                <div className="flex items-center gap-2 text-white/90">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="text-sm font-medium tracking-wide">{t.cards.recall.tag}</span>
                </div>
              </div>
            </div>
            <div className="p-8 flex-1 flex flex-col items-center text-center bg-white relative z-20">
              <h3 className="text-lg font-bold text-gray-800 mb-4 leading-tight">{t.cards.recall.title} <br /><span className="text-gray-400 text-sm font-normal">{t.cards.recall.subtitle}</span></h3>
              <p className="text-gray-500 text-sm mb-8 leading-relaxed font-light line-clamp-3">
                {t.cards.recall.desc}
              </p>
              <Link to="/book-service" className="mt-auto w-full">
                <Button className="w-full rounded-full bg-black text-white hover:bg-red-600 transition-all duration-300 py-6 text-sm tracking-wider uppercase font-medium shadow-lg hover:shadow-red-600/30">
                  {t.cards.recall.btn}
                </Button>
              </Link>
            </div>
          </div>

        </div>
      </div>



    </div>
  );
};

export default Service;